/**
 * Validates if a URL is safe for redirection
 * @param {string} url - The URL to validate
 * @return {boolean} - True if URL is safe, false otherwise
 */
export const isValidRedirectUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const urlObj = new URL(url);

    // List of trusted domains for redirect
    const trustedDomains = [
      'pandora.net',
      'msappproxy.net',
      'pandoranet.msappproxy.net',
      'hrmobileappuat-pandoranet.msappproxy.net',
      'hrmobileapp-pandoranet.msappproxy.net',
      // Add other trusted domains as needed
    ];

    // Check if protocol is safe
    const safeProtocols = ['https:', 'http:'];
    if (!safeProtocols.includes(urlObj.protocol)) {
      return false;
    }

    // Check if domain is trusted
    const isTrustedDomain = trustedDomains.some((domain) =>
      urlObj.hostname.endsWith(domain) || urlObj.hostname === domain,
    );

    return isTrustedDomain;
  } catch (error) {
    // Invalid URL format
    console.error('Invalid URL format:', error);
    return false;
  }
};

/**
 * Sanitizes a URL for use in img src or other attributes
 * Prevents XSS by validating and restricting allowed URL patterns
 * @param {string} url - The URL to sanitize
 * @return {string|null} - Sanitized URL or null if invalid
 */
export const sanitizeImageUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return null;
  }

  try {
    // For data URLs, ensure they're images only
    if (url.startsWith('data:')) {
      if (!url.startsWith('data:image/')) {
        console.warn('Blocked non-image data URL');
        return null;
      }
      // Validate data URL format
      const dataUrlPattern = /^data:image\/(png|jpg|jpeg|gif|webp|svg\+xml);base64,/;
      if (!dataUrlPattern.test(url)) {
        console.warn('Invalid data URL format');
        return null;
      }
      return url;
    }

    // For regular URLs, validate them
    const urlObj = new URL(url);

    // Only allow http/https protocols for images
    if (!['https:', 'http:'].includes(urlObj.protocol)) {
      console.warn('Blocked unsafe protocol:', urlObj.protocol);
      return null;
    }

    // For images, we allow any HTTPS domain (images don't cause redirects)
    // but we still validate the URL structure to prevent XSS
    return urlObj.href;
  } catch (error) {
    console.error('Invalid image URL:', error);
    return null;
  }
};

/**
 * Safely redirects to a URL after validation
 * Uses allowlist approach to prevent open redirect vulnerabilities
 * @param {string} url - The URL to redirect to
 * @param {string} fallbackUrl - Fallback URL if validation fails
 */
export const safeRedirect = (url, fallbackUrl = '/') => {
  // Strict validation before any redirect
  if (!url || typeof url !== 'string') {
    console.warn('Invalid redirect URL provided, using fallback');
    window.location.assign(fallbackUrl);
    return;
  }

  try {
    // Validate against allowlist
    const isValid = isValidRedirectUrl(url);

    if (isValid) {
      // Reconstruct URL to prevent manipulation
      const validatedUrl = new URL(url);
      const safeUrl = validatedUrl.href;

      // Additional check: ensure no dangerous URL schemes slipped through
      const dangerousSchemes = ['javascript:', 'data:', 'vbscript:', 'file:', 'about:'];
      const urlLower = safeUrl.toLowerCase();

      for (const scheme of dangerousSchemes) {
        if (urlLower.startsWith(scheme)) {
          throw new Error(`Unsafe URL scheme detected: ${scheme}`);
        }
      }

      window.location.assign(safeUrl);
    } else {
      throw new Error('URL not in allowlist');
    }
  } catch (error) {
    console.warn('Blocked redirect to untrusted URL:', url, error.message);
    // Use fallback URL which should be a relative path
    window.location.assign(fallbackUrl);
  }
};
